import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  X, User, Phone, Briefcase, MapPin, Mail, Shield, Clock, Clock3Icon, Building2,
  Hash, PhoneCall, FileText, Award, Calendar, Home, CreditCard,
  GraduationCap, Pencil, Download, Eye, FileCheck, ExternalLink, FileCode,
  Upload, Check, XCircle, Loader2, ShieldAlert, BadgeCheck, ShieldCheck,
  MinusCircle, CalendarClock, CalendarCheck, CalendarX, TrendingUp, TrendingDown,
  Flag, AlertCircle, History
} from 'lucide-react';
import { fetchContractorTerminations } from '../../store/slices/contractorTerminationSlice';
import { fetchAllEmployees } from '../../store/slices/employeeSlice';
import { getDivisionList } from '../../store/slices/divisionSlice';

import tenant from '../../config/tenant';
import EmptyState from '../common/EmptyState';
import { PENDING_GRADIENT as PENDING_GRADIENT_CLS } from '../../constants/pendingTheme';
import { isPendingApproval } from '../../utils/employeeApproval';
import { formatExperienceLong } from '../../utils/employeeValidation';
import { isBankApproved, isBankPending, isBankActive, isBankPrimary, pendingBanks as pendingBanksOf, bankKey } from '../../utils/employeeBankApproval';
import {
  getEmployeeProfilePicUrl,
  extractEmployeeDocuments,
  downloadFile,
  getLocationName,
  getCostCenter,
  RG,
  isContractCategory,
} from '../../utils/employeeUtils';
import regionService from '../../services/RegionService';
import { useAuthedFileUrl, fetchAuthedBlobUrl } from '../../utils/authedFile';
import { isEmergencyContact, CONTACT_TYPE_EMERGENCY } from '../../utils/contactType';

const IMAGE_BASE = tenant?.api?.employeeImageBaseUrl;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const d = (v) => {
  if (v === null || v === undefined || v === '' || v === 'null' || v === 'undefined') return null;
  const s = String(v).trim();
  return s === '' || s === '0' ? null : s;
};
const fmt  = (v) => d(v) || '—';
const fmtDate = (v) => {
  const raw = d(v);
  if (!raw) return '—';
  try {
    const dt = new Date(v);
    if (isNaN(dt.getTime())) return raw;
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return raw; }
};
const fmtGender = (v) => v === 'M' ? 'Male' : v === 'F' ? 'Female' : fmt(v);

// ─── Decode maps ──────────────────────────────────────────────────────────────
const RELIGION_MAP = {
  B: 'Buddhism', C: 'Christian', H: 'Hindu', I: 'Islam', O: 'Other',
  '1': 'Buddhism', '2': 'Christian', '3': 'Hindu', '4': 'Islam', '5': 'Other'
};
const MARITAL_MAP  = { S: 'Single', M: 'Married', D: 'Divorced', W: 'Widowed' };
const BLOOD_MAP    = {
  '1': 'A+', '2': 'A-', '3': 'B+', '4': 'B-',
  '5': 'AB+', '6': 'AB-', '7': 'O+', '8': 'O-',
  '0': 'Group O', '9': 'Group B', '10': 'Group B+', '11': 'Group B-', '12': 'Unknown',
};

function dec(map, v) {
  const raw = d(v);
  if (!raw) return '—';
  return map[raw] || raw;
}

function calcYears(date) {
  if (!date) return null;
  const s = new Date(date);
  if (isNaN(s.getTime())) return null;
  const now = new Date();
  let y = now.getFullYear() - s.getFullYear();
  if (now.getMonth() - s.getMonth() < 0 ||
     (now.getMonth() - s.getMonth() === 0 && now.getDate() < s.getDate())) y--;
  return y >= 0 ? y : null;
}

// ─── Field row ────────────────────────────────────────────────────────────────
function FR({ label, value, highlight = false, mono = false, action = null }) {
  // A caller can pass a node (a status pill, say) instead of text.
  const isNode = React.isValidElement(value);
  const val = isNode ? value : (typeof value === 'string' ? value : fmt(value));

  if (val === '—' && !action) return (
    <div className="flex items-start gap-2 py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-[11px] text-slate-400 w-36 shrink-0 leading-5 font-medium">{label}</span>
      <span className="flex items-center gap-1.5 text-[11px] text-slate-300 flex-1 leading-5">
        <MinusCircle size={11} className="shrink-0" />
        <span className="italic">Not recorded</span>
      </span>
    </div>
  );
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-slate-50 last:border-0">
      <div className="flex items-start gap-2 flex-1 min-w-0">
        <span className="text-[11px] text-slate-500 w-36 shrink-0 leading-5 font-medium">{label}</span>
        <span className={`text-[12px] flex-1 leading-5 break-words
          ${highlight ? 'font-bold text-slate-900' : 'font-medium text-slate-700'}
          ${mono ? 'font-mono' : ''}`}>
          {val}
        </span>
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

function SH({ label, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-2 mt-4 first:mt-0">
      {Icon && <Icon size={12} className="text-blue-600" />}
      <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">{label}</span>
      <div className="flex-1 h-px bg-blue-100" />
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color = 'green' }) {
  const colors = {
    blue:   { bg: 'bg-blue-50',   text: 'text-blue-700',   icon: 'text-blue-500'   },
    green:  { bg: 'bg-primary-50',  text: 'text-primary-700',  icon: 'text-primary-500'  },
    amber:  { bg: 'bg-amber-50',  text: 'text-amber-700',  icon: 'text-amber-500'  },
    purple: { bg: 'bg-violet-50', text: 'text-violet-700', icon: 'text-violet-500' },
    teal:   { bg: 'bg-primary-50',   text: 'text-primary-700',   icon: 'text-primary-500'   },
  };
  const c = colors[color] || colors.blue;
  if (!value || value === '—') return null;
  return (
    <div className={`${c.bg} rounded-lg px-3 py-2.5 flex items-center gap-2.5`}>
      <Icon size={15} className={c.icon} />
      <div>
        <p className="text-[10px] text-slate-500 font-medium leading-none mb-0.5">{label}</p>
        <p className={`text-[12px] font-bold ${c.text} leading-tight`}>{value}</p>
      </div>
    </div>
  );
}

const TABS = [
  { key: 'overview',   label: 'Overview',   icon: User          },
  { key: 'documents',  label: 'Documents',  icon: FileText      },
  { key: 'contact',    label: 'Contact',    icon: Phone         },
  { key: 'employment', label: 'Employment', icon: Briefcase     },
  { key: 'bank',       label: 'Bank',       icon: CreditCard    },
  { key: 'qualify',    label: 'Education',  icon: GraduationCap },
  // Only shown when HEC_CATEGORY_NAME is Contract.
  { key: 'contract',   label: 'Contract Details', icon: CalendarClock, contractOnly: true },
];

const PENDING_GRADIENT = PENDING_GRADIENT_CLS;

// ─── ModalImage ───────────────────────────────────────────────────────────────
function ModalImage({ directUrl, serviceNo, initials, gradientClassName, gradientStyle }) {
  const images = useSelector((s) => s.images?.images || {});
  const uploadedImage = serviceNo ? images[String(serviceNo).trim()] : null;
  const [imgSrc, setImgSrc]   = useState(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed]   = useState(false);

  /* Same reason as the card avatar: the picture endpoint wants the auth-key
     header, which an <img> tag cannot send. Fetch it, then show the blob. */
  useEffect(() => {
    let cancelled = false;
    if (uploadedImage) { setImgSrc(uploadedImage); setFailed(false); setLoading(false); return undefined; }

    const target = directUrl || (serviceNo ? `${IMAGE_BASE}?serviceNo=${encodeURIComponent(serviceNo)}` : null);
    if (!target) { setFailed(true); setLoading(false); return undefined; }

    setLoading(true); setFailed(false);

    fetchAuthedBlobUrl(target)
      .then((src) => { if (!cancelled) { setImgSrc(src); setLoading(false); } })
      .catch(() => { if (!cancelled) { setFailed(true); setLoading(false); } });

    return () => { cancelled = true; };
  }, [directUrl, serviceNo, uploadedImage]);

  const cls = "w-20 h-20 rounded-xl object-cover shadow-lg ring-2 ring-white/50";
  if (uploadedImage) return <img src={uploadedImage} alt={initials} className={cls} />;
  if (imgSrc && !failed) return <img src={imgSrc} alt={initials} className={cls} onError={() => setFailed(true)} />;
  if (loading) return (
    <div className={`w-20 h-20 rounded-xl ${gradientClassName} flex items-center justify-center shadow-lg`} style={gradientStyle}>
      <div className="w-5 h-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
    </div>
  );
  return (
    <div className={`w-20 h-20 rounded-xl ${gradientClassName} flex items-center justify-center shadow-lg ring-2 ring-white/30`} style={gradientStyle}>
      <span className="text-white font-bold text-2xl">{initials}</span>
    </div>
  );
}

// ─── Document Preview Lightbox ─────────────────────────────────────────────────

function ConfirmDecision({ open, kind, name, includeEmployee, bankCount, busy, onCancel, onConfirm }) {
  if (!open) return null;
  const isReject = kind === 'reject';

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={busy ? undefined : onCancel} />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 pt-6 pb-4 flex items-start gap-3.5">
          <span className={`p-2.5 rounded-xl shrink-0 ${isReject ? 'bg-red-50 text-red-600' : 'bg-primary-50 text-primary-600'}`}>
            {isReject ? <XCircle size={20} /> : <ShieldCheck size={20} />}
          </span>
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-slate-900 leading-tight">
              {isReject ? 'Reject this employee?' : 'Approve this employee?'}
            </h3>
            <p className="text-[12px] text-slate-500 mt-1 leading-relaxed">
              Are you sure you want to {isReject ? 'reject' : 'approve'}{' '}
              <span className="font-semibold text-slate-700">{name}</span>?
            </p>

            {/* Spell out what will actually be sent, so nothing is a surprise. */}
            {!isReject && (
              <ul className="mt-3 space-y-1.5">
                {includeEmployee && (
                  <li className="flex items-center gap-2 text-[12px] text-slate-600">
                    <Check size={12} className="text-primary-600 shrink-0" />
                    The employee record will be marked approved.
                  </li>
                )}
                {bankCount > 0 && (
                  <li className="flex items-center gap-2 text-[12px] text-slate-600">
                    <Check size={12} className="text-primary-600 shrink-0" />
                    {bankCount} bank account{bankCount !== 1 ? 's' : ''} will be approved.
                  </li>
                )}
              </ul>
            )}
          </div>
        </div>

        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 rounded-lg text-[12px] font-semibold bg-white border border-slate-200 text-slate-600
              hover:bg-slate-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            No, cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            autoFocus
            className={`flex items-center gap-1.5 px-5 py-2 rounded-lg text-[12px] font-bold text-white shadow-sm transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
              ${isReject ? 'bg-red-600 hover:bg-red-700' : 'bg-primary-600 hover:bg-primary-700'}`}
          >
            {busy
              ? <Loader2 size={14} className="animate-spin" />
              : isReject ? <XCircle size={14} /> : <Check size={14} />}
            {busy ? 'Saving…' : isReject ? 'Yes, reject' : 'Yes, approve'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DocumentPreviewModal({ doc, onClose }) {
  /* The document endpoints need the auth-key header, and <img>/<iframe> cannot
     send one — so the file is fetched here and shown from a blob URL. */
  const { src, loading, error } = useAuthedFileUrl(doc ? doc.url : null);

  if (!doc) return null;
  const isImage = doc.isImg;
  const isPdf   = doc.isPdf;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md">
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3.5 bg-slate-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <FileText size={18} className="text-blue-400" />
            <div>
              <h3 className="text-sm font-bold truncate max-w-md">{doc.title}</h3>
              <p className="text-[11px] text-slate-400 truncate">{doc.fileName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadFile(doc.url, doc.fileName)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
            >
              <Download size={13} /> Download
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-slate-400 hover:text-white rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content Viewer */}
        <div className="flex-1 overflow-auto bg-slate-100 p-4 flex items-center justify-center min-h-[400px]">
          {loading ? (
            <div className="flex flex-col items-center gap-3 text-slate-400 py-12">
              <Loader2 size={28} className="animate-spin" />
              <span className="text-xs">Loading document…</span>
            </div>
          ) : error ? (
            <div className="text-center py-12 px-6">
              <ShieldAlert size={40} className="mx-auto text-amber-500 mb-3" />
              <p className="text-sm font-semibold text-slate-700 mb-1">Could not open this document</p>
              <p className="text-xs text-slate-500">{error}</p>
            </div>
          ) : isImage ? (
            <img
              src={src}
              alt={doc.title}
              className="max-h-[70vh] max-w-full object-contain rounded-lg shadow-md"
            />
          ) : isPdf ? (
            <iframe
              src={src}
              title={doc.title}
              className="w-full h-[70vh] rounded-lg border border-slate-300"
            />
          ) : (
            <div className="text-center py-12 px-6">
              <FileText size={48} className="mx-auto text-slate-400 mb-3" />
              <p className="text-sm font-semibold text-slate-700 mb-1">Preview not available for this file type</p>
              <p className="text-xs text-slate-500 mb-4">Click download to save and open the file locally.</p>
              <button
                onClick={() => downloadFile(doc.url, doc.fileName)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-xl shadow transition-colors"
              >
                <Download size={14} /> Download Document
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Overview Panel ───────────────────────────────────────────────────────────
function OverviewPanel({ e, onPreviewDoc }) {
  const joinDate   = d(e.HED_DATE_OF_JOINING) || d(e.p_recruitment_date);
  const yearsWorked = calcYears(joinDate);

  const deptName   = d(e.HDD_DEPARTMENT_NAME) || d(e.p_department_code) || '—';
  const locName    = getLocationName(e) || '—';
  const desig      = d(e.HED_DESIGNATION_NAME) || d(e.p_designation) || '—';

  const firstName  = d(e.HED_FIRST_NAME) || d(e.p_first_name) || '';
  const midName    = d(e.HED_MIDDLE_NAME) || '';
  const lastName   = d(e.HED_LAST_NAME) || d(e.p_last_name) || '';
  const title      = d(e.HED_TITLE) || d(e.p_title) || '';
  const fullName   = [title, firstName, midName, lastName].filter(Boolean).join(' ');
  const reportName = d(e.HED_REPORT_NAME) || d(e.p_report_name) || fullName || '—';

  const dob        = d(e.HED_DATE_OF_BIRTH) || d(e.p_birth_date);
  const nic        = d(e.HED_NIC_NO) || d(e.p_nid) || '—';
  const epf        = d(e.HED_EPF_NO) || d(e.p_epf) || '—';
  const etf        = d(e.HED_EPF_NO) || d(e.p_epf) || '—';
  const passport   = d(e.HED_PASSPORT_NO) || d(e.p_passport) || '—';
  const tin        = d(e.HED_TIN_NO) || d(e.p_TinNo) || '—';
  const gender     = d(e.HED_GENDER) || d(e.p_sex) || '';
  const marital    = d(e.HED_MARITAL_STATUS) || d(e.p_marital_status) || '';
  const religion   = d(e.HED_RELIGION) || d(e.p_religion) || '';
  const blood      = d(e.HED_BLOOD_GROUP) || d(e.p_blood_group) || '';
  /* 0 children is a real answer, so it must not be swallowed by `||`. */
  const children   = e.HED_NO_OF_CHILDREN === null || e.HED_NO_OF_CHILDREN === undefined
                       ? '' : String(e.HED_NO_OF_CHILDREN);
  /* Stored compact ("7Y5M"), read out in full ("7 Years 5 Months"). */
  const totalExp   = formatExperienceLong(d(e.HED_TOTAL_EXPERIANCE)) || '';

  const docs = extractEmployeeDocuments(e);
  const nicDoc = docs.find(d => d.category === 'nic');
  const birthCertDoc = docs.find(d => d.category === 'birth_certificate');
  const passportDoc = docs.find(d => d.category === 'passport');

  const docActionButton = (doc, defaultTitle) => {
    if (!doc) return null;
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPreviewDoc(doc)}
          title={`View ${doc.title || defaultTitle}`}
          className="p-1 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
        >
          <Eye size={13} />
        </button>
        <button
          onClick={() => downloadFile(doc.url, doc.fileName)}
          title={`Download ${doc.title || defaultTitle}`}
          className="p-1 text-slate-500 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
        >
          <Download size={13} />
        </button>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <StatBox icon={Hash}      label="Service No"   value={fmt(e.HED_SERVICE_NO || e.p_ced_service_no)} color="blue"   />
        <StatBox icon={Briefcase} label="Designation"  value={desig}     color="purple" />
        <StatBox icon={MapPin}    label="Location"     value={locName}   color="teal"   />
        {yearsWorked !== null && <StatBox icon={Clock} label="Years Joined" value={`${yearsWorked} yr${yearsWorked !== 1 ? 's' : ''}`} color="green" />}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
        <div>
          <SH label="Personal Details" icon={User} />
          <FR label="Full Name"      value={fullName || reportName} highlight />
          <FR label="Report Name"    value={reportName} />
          <FR label="Initials"       value={fmt(e.HED_INITIALS || e.p_initials)} />
          <FR label="Gender"         value={fmtGender(gender)} />
          <FR
            label="Date of Birth"
            value={fmtDate(dob)}
            action={docActionButton(birthCertDoc, 'Birth Certificate')}
          />
          <FR label="Marital Status" value={dec(MARITAL_MAP, marital)} />
          <FR label="Religion"       value={dec(RELIGION_MAP, religion)} />
          <FR label="Blood Group"    value={dec(BLOOD_MAP, blood)} />
          <FR label="No of Children" value={fmt(children)} />
          {totalExp && <FR label="Total Experience" value={totalExp} />}
        </div>
        <div>
          <SH label="Identification" icon={Shield} />
          <FR
            label="NIC"
            value={nic}
            mono
            action={docActionButton(nicDoc, 'NIC Document')}
          />
          <FR label="EPF No."        value={epf} mono />
          <FR label="ETF No."        value={etf} mono />
          <FR label="TIN No."        value={tin} mono />
          <FR
            label="Passport"
            value={passport}
            mono
            action={docActionButton(passportDoc, 'Passport Copy')}
          />
          {/* <SH label="Department" icon={Building2} />
          <FR label="Department"     value={deptName} highlight />
          <FR label="Group"          value={fmt(e.HED_GROUP)} />
          <FR label="Cost Center"    value={fmt(e.HED_COSTCENTER)} /> */}
        </div>
      </div>
    </div>
  );
}

// ─── Documents Panel ──────────────────────────────────────────────────────────
function DocumentsPanel({ e, onPreviewDoc, onEdit }) {
  const docs = extractEmployeeDocuments(e);
  const nicDoc       = docs.find(d => d.category === 'nic');
  const birthCertDoc = docs.find(d => d.category === 'birth_certificate');
  const qualDoc      = docs.find(d => d.category === 'qualification');
  const traineeCertDoc = docs.find(d => d.category === 'trainee_certificate');
  const otherCertDoc   = docs.find(d => d.category === 'other_certificate');
  const jobDescDoc     = docs.find(d => d.category === 'job_description');
  const profileDoc   = docs.find(d => d.category === 'profile_pic');
  const otherDocs    = docs.filter(d => !['nic', 'birth_certificate', 'qualification', 'trainee_certificate', 'other_certificate', 'job_description', 'profile_pic'].includes(d.category));

  const standardDocs = [
    { key: 'birth_certificate', title: 'Birth Certificate', doc: birthCertDoc, icon: FileText, reqName: 'Birth Certificate' },
    { key: 'nic',               title: 'NIC Copy / Document', doc: nicDoc,       icon: Shield,   reqName: 'National ID (NIC)' },
    { key: 'qualification',     title: 'Educational Qualification Certificate', doc: qualDoc, icon: GraduationCap, reqName: 'Qualification Certificate' },
    { key: 'trainee_certificate', title: 'Trainee Certificate', doc: traineeCertDoc, icon: FileText, reqName: 'Trainee Certificate' },
    { key: 'other_certificate',  title: 'Other Certificates',  doc: otherCertDoc,  icon: FileText, reqName: 'Other Certificates' },
    { key: 'job_description',    title: 'Job Description',     doc: jobDescDoc,    icon: FileText, reqName: 'Job Description' },
    { key: 'profile_pic',       title: 'Profile Photo',     doc: profileDoc,   icon: User,     reqName: 'Profile Photo' },
  ];

  return (
    <div className="space-y-5">
      {/* Header bar */}
      <div className="flex items-center justify-between px-4 py-3 bg-blue-50 rounded-xl border border-blue-100">
        <div className="flex items-center gap-2">
          <FileCheck size={16} className="text-blue-600" />
          <span className="text-xs font-bold text-blue-900">Employee Documents & Certificates</span>
        </div>
        <div>
          <span className="text-[11px] font-semibold bg-blue-100 text-blue-800 px-2.5 py-0.5 rounded-full">
            {docs.length} Document{docs.length !== 1 ? 's' : ''} Uploaded
          </span>
        </div>
      </div>

      {/* Main Standard Documents Grid */}
      <div>
        <SH label="Required Documents" icon={Shield} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {standardDocs.map(({ key, title, doc, icon: Icon, reqName }) => {
            const hasDoc = Boolean(doc);
            return (
              <div
                key={key}
                className={`p-4 rounded-xl border transition-all ${
                  hasDoc
                    ? 'bg-white border-slate-200 shadow-sm hover:border-blue-300 hover:shadow-md'
                    : 'bg-slate-50/70 border-slate-200 border-dashed'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className={`p-2.5 rounded-xl shrink-0 ${hasDoc ? 'bg-blue-50 text-blue-600' : 'bg-slate-200 text-slate-400'}`}>
                      <Icon size={20} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h4 className="text-xs font-bold text-slate-800 leading-snug">{title}</h4>
                      {hasDoc ? (
                        <>
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-primary-600 bg-primary-50 px-2 py-0.5 rounded-md mt-1">
                            Available
                          </span>
                          <p className="text-[11px] text-slate-500 truncate mt-1 font-mono" title={doc.fileName}>
                            {doc.fileName}
                          </p>
                        </>
                      ) : (
                        <>
                          <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md mt-1">
                            Not Uploaded
                          </span>
                          <p className="text-[11px] text-slate-400 mt-1 italic">
                            No {reqName} on record
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {hasDoc && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => onPreviewDoc(doc)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-slate-100 hover:bg-blue-50 text-slate-700 hover:text-blue-700 text-xs font-semibold rounded-lg transition-colors"
                        title="View document"
                      >
                        <Eye size={12} /> View
                      </button>
                      <button
                        onClick={() => downloadFile(doc.url, doc.fileName)}
                        className="flex items-center gap-1 px-2.5 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-semibold rounded-lg shadow-sm transition-colors"
                        title="Download file"
                      >
                        <Download size={12} /> Download
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Additional / Qualification Documents */}
      {otherDocs.length > 0 && (
        <div>
          <SH label="Additional Attachments & Qualifications" icon={GraduationCap} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {otherDocs.map((doc) => (
              <div key={doc.id} className="p-3.5 bg-white rounded-xl border border-slate-200 shadow-sm flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg shrink-0">
                    <FileText size={16} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-slate-800 truncate" title={doc.title}>{doc.title}</p>
                    <p className="text-[10px] text-slate-400 font-mono truncate" title={doc.fileName}>{doc.fileName}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => onPreviewDoc(doc)}
                    className="p-1.5 text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                    title="View"
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    onClick={() => downloadFile(doc.url, doc.fileName)}
                    className="p-1.5 text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                    title="Download"
                  >
                    <Download size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Contact Panel ────────────────────────────────────────────────────────────
function ContactPanel({ e }) {
  const addresses = e.Addresses || [];
  const contacts  = e.Contacts  || [];
  const permanent = addresses.find(a => a.HEA_ADDRESS_TYPE === 'P' || a.HEA_ADDRESS_TYPE === 'C');


  const heaVal = (keys) => {
    if (!permanent) return '';
    for (const k of keys) {
      if (permanent[k] !== undefined && permanent[k] !== null && String(permanent[k]).trim() !== '') {
        return String(permanent[k]).trim();
      }
    }
    return '';
  };

  const provinceCode   = heaVal(['HEA_PROVINCE_ID', 'HEA_PROVINCE_CODE', 'PROVINCE_CODE']);
  const districtCode   = heaVal(['HEA_DISTRICT_ID', 'HEA_DISTRICT_CODE', 'DISTRICT_CODE']);
  const electorateCode = heaVal(['HEA_ELECTORATE_CODE', 'HEA_ELECTORATE_ID', 'ELECTORATE_CODE']);
  const stationCode    = heaVal(['HEA_STAION_CODE', 'HEA_STATION_CODE', 'HPD_STATION_CODE']);
  const gramaCode      = heaVal(['HEA_GRAMASEWAKA_CODE', 'HEA_GRAMA_CODE', 'GRAMASEWAKA_CODE']);

  const provinceLabel   = heaVal(['HPD_PROVINCE_NAME', 'PROVINCE_NAME']);
  const districtLabel   = heaVal(['HDD_DISTRICT_NAME', 'DISTRICT_NAME']);
  const electorateLabel = heaVal(['ELECTORATE_AREA', 'ELECTORATE_NAME']);
  const stationLabel    = heaVal(['HPD_POLICESTATION_NAME', 'POLICESTATION_NAME', 'STATION_NAME']);
  const gramaLabel      = heaVal(['HGD_GRAMASEWAKADIVISION_NAME', 'HGD_GRAMASEWAKA_DIVISION_NAME',
                                  'GRAMASEWAKA_DIVISION_NAME', 'DIVISION_NAME']);


  const needsLookup =
    (!!provinceCode && !provinceLabel) || (!!districtCode && !districtLabel) ||
    (!!electorateCode && !electorateLabel) || (!!stationCode && !stationLabel) ||
    (!!gramaCode && !gramaLabel);

  const [rg, setRg] = useState({ provinces: [], districts: [], electorates: [], stations: [], gramas: [] });
  useEffect(() => {
    if (!needsLookup) return undefined;
    let alive = true;
    const rows = (r) => (Array.isArray(r?.data?.ResultSet) ? r.data.ResultSet : []);
    Promise.all([
      regionService.GetProvinceList().catch(() => null),
      regionService.GetDistrictList().catch(() => null),
      regionService.GetElectorateList().catch(() => null),
      regionService.GetPoliceStationList().catch(() => null),
      regionService.GetGramaList().catch(() => null),
    ]).then(([p, d, el, st, gr]) => {
      if (!alive) return;
      setRg({ provinces: rows(p), districts: rows(d), electorates: rows(el),
              stations: rows(st), gramas: rows(gr) });
    });
    return () => { alive = false; };
  }, [needsLookup]);

  /* Name from the row, else resolved from the master list, else the bare code. */
  const label = (given, list, code, codeOf, nameOf) => {
    if (given) return given;
    if (!code) return '';
    const hit = list.find((r) => String(codeOf(r)) === String(code));
    return (hit && nameOf(hit)) || code;
  };

  const provinceName   = label(provinceLabel,   rg.provinces,   provinceCode,   RG.provCode, RG.provName);
  const districtName   = label(districtLabel,   rg.districts,   districtCode,   RG.distCode, RG.distName);
  const electorateName = label(electorateLabel, rg.electorates, electorateCode, RG.elecCode, RG.elecArea);
  const stationName    = label(stationLabel,    rg.stations,    stationCode,    RG.stnCode,  RG.stnName);
  const gramaName      = label(gramaLabel,      rg.gramas,      gramaCode,      RG.gramaCode, RG.gramaName);

  const hasRegion = !!(provinceName || districtName || electorateName || stationName || gramaName);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
      <div>
        <SH label="Phone Numbers" icon={Phone} />
        <FR label="Mobile"         value={fmt(e.HED_MOBILE_NO || e.p_mobile)} />
        <FR label="Home Phone"     value={fmt(e.HED_HOME_NO || e.p_home_tel)} />
        <FR label="Office Phone"   value={fmt(e.HED_OFFICE_NO || e.p_office_tel)} />

        <SH label="Email" icon={Mail} />
        <FR label="Personal Email" value={fmt(e.HED_PERSONAL_EMAIL || e.p_personal_email)} />
        <FR label="Company Email"  value={fmt(e.HED_COMPANY_EMAIL  || e.p_email)} />

        {hasRegion && (
          <>
            <SH label="Region" icon={MapPin} />
            <FR label="Province"       value={fmt(provinceName)} />
            <FR label="District"       value={fmt(districtName)} />
            <FR label="Electorate"     value={fmt(electorateName)} />
            <FR label="Police Station" value={fmt(stationName)} />
            <FR label="Grama Division" value={fmt(gramaName)} />
          </>
        )}

        {permanent && (
          <>
            <SH label="Address" icon={Home} />
            <FR label="Line 1"   value={fmt(permanent.HEA_ADDRESS_LINE1)} />
            <FR label="Line 2"   value={fmt(permanent.HEA_ADDRESS_LINE2)} />
            <FR label="Line 3"   value={fmt(permanent.HEA_ADDRESS_LINE3)} />
            <FR label="City"     value={fmt(permanent.HEA_CITY)} />
            <FR label="Postal"   value={fmt(permanent.HEA_POSTAL_CODE)} />
          </>
        )}
      </div>
      <div>
        {contacts.length > 0 ? (
          <>
            <SH label="Contact & Emergency" icon={PhoneCall} />
            {contacts.map((c, i) => (
              <div key={c.HEC_CONTACT_ID || i} className="mb-3 pl-2 border-l-2 border-blue-100">
                <FR label="Type"         value={isEmergencyContact(c.HEC_CONTACT_TYPE || CONTACT_TYPE_EMERGENCY) ? 'Emergency contact' : 'Contact'} />
                <FR label="Name"         value={fmt(c.HEC_CONTACT_NAME)} />
                <FR label="Number"       value={fmt(c.HEC_CONTACT_NUMBER)} />
                <FR label="Relationship" value={fmt(c.HEC_RELATIONSHIP)} />
                <FR label="Address"      value={fmt(c.HEC_ADDRESS)} />
              </div>
            ))}
          </>
        ) : (
          <>
            <SH label="Contact & Emergency" icon={PhoneCall} />
            <EmptyState
              compact
              title="No contacts"
              message="At least one contact is worth having on file for every employee."
            />
          </>
        )}
      </div>
    </div>
  );
}

// ─── Employment Panel ─────────────────────────────────────────────────────────
function EmploymentPanel({ e }) {
  // Division: the employee stores HED_DIV_CODE; the division list gives its name.
  const dispatch = useDispatch();
  const divisions = useSelector((s) => s.divisionSlice?.divisions || []);
  useEffect(() => {
    if (!divisions.length) dispatch(getDivisionList());
  }, [dispatch, divisions.length]);
  const divCode = String(d(e.HED_DIV_CODE) || d(e.HDD_DIV_CODE) || '').trim();
  const divDesc = divCode
    ? (divisions.find((r) => String(r.HDD_DIV_CODE ?? '').trim() === divCode)?.HDD_DIV_DESC || '')
    : '';
  const divisionLabel = divCode ? (divDesc ? `${divDesc} (${divCode})` : divCode) : '';

  const joinDate  = d(e.HED_DATE_OF_JOINING) || d(e.p_recruitment_date);
  const recruitDate = d(e.HED_RECRUITMENT_DATE);
  const promoDate   = d(e.HED_PROMOTED_DATE);
  const permDate    = d(e.HED_PERMANENT_DATE);
  const retireDate  = d(e.HED_RETIREMENT_DATE);
  const terminationDate = d(e.HED_TERMINATION_DATE);
  const isContract  = isContractCategory(e.HEC_CATEGORY_NAME || e.EmploymentCategoryName || e.employmentCategoryName);
  const confDate  = d(e.HED_CONFIRMATION_DATE) || d(e.p_permanent_date);
  const yearsWorked = calcYears(joinDate);

  const isActive = e.HED_IS_ACTIVE === true ||
    String(e.HED_IS_ACTIVE ?? '').trim().toLowerCase() === 'true';
  const leavingDate   = d(e.HED_LEAVING_DATE);
  const leavingReason = d(e.HED_LEAVING_REASON);

  /* Active/Inactive  */
  const hasEmploymentData = [
    getLocationName(e), getCostCenter(e),
    e.HDD_DEPARTMENT_NAME, e.p_department_code, e.HED_DIV_CODE,
    e.HED_DESIGNATION_NAME, e.p_designation,
    e.HGD_GRADE_NAME, e.HGD_GRADE_CODE,
    e.HEC_CATEGORY_NAME, e.HED_EMPLOYMENT_CATEGORY_ID,
    e.HED_NOTICE_PERIOD_DAYS, e.HED_OT_RATE,
    e.HSD_SHIFT_NAME, e.HSD_SHIFT_CODE, e.HSD_START_TIME, e.HSD_END_TIME,
    e.HGD_MIN_SALARY, e.HGD_MAX_SALARY,
    joinDate,
  ].some((v) => d(v));

  if (!hasEmploymentData) return (
    <EmptyState
      title="No employment details yet"
      message="Department, designation, grade, shift and join date are set through Edit. Payroll needs them before this employee can be processed."
    />
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
      <div>
        <SH label="Organisation" icon={Building2} />
        <FR label="Location"      value={fmt(getLocationName(e))} highlight />
        <FR label="Cost Center"   value={fmt(getCostCenter(e))} />
        <FR label="Division"      value={divisionLabel || '—'} />
        <FR label="Department"    value={fmt(e.HDD_DEPARTMENT_NAME || e.p_department_code)} />
        <FR label="Designation"   value={fmt(e.HED_DESIGNATION_NAME || e.p_designation)} />
        <FR label="Grade (Work Category)"         value={fmt(e.HGD_GRADE_NAME)} />
        <FR label="Grade Code"    value={fmt(e.HGD_GRADE_CODE)} />
        <FR label="Employee Category (Present Employer)" value={fmt(e.HEC_CATEGORY_NAME || e.HedEmploymentCategoryName || e.EmploymentCategoryName || e.employmentCategoryName || e.p_cat_code || (e.HED_EMPLOYMENT_CATEGORY_ID ? `Category #${e.HED_EMPLOYMENT_CATEGORY_ID}` : ''))} />
        <FR label="Notice Period" value={e.HED_NOTICE_PERIOD_DAYS ? `${e.HED_NOTICE_PERIOD_DAYS} days` : '—'} />


        <SH label="Key Dates" icon={Calendar} />
        <FR label="Join Date"        value={fmtDate(joinDate)} />
        {/* <FR label="Confirmation"     value={fmtDate(confDate)} /> */}
        {isContract ? (
          <FR label="Termination Date" value={fmtDate(terminationDate)} />
        ) : (
          <>
            <FR label="Recruitment Date" value={fmtDate(recruitDate)} />
            <FR label="Promoted Date"    value={fmtDate(promoDate)} />
            <FR label="Permanent Date"   value={fmtDate(permDate)} />
            <FR label="Retirement Date"  value={fmtDate(retireDate)} />
          </>
        )}
        <FR label="Years of Service" value={yearsWorked !== null ? `${yearsWorked} yr${yearsWorked !== 1 ? 's' : ''}` : '—'} />
      </div>
      <div>
        <SH label="Employment Status" icon={Shield} />
        <FR
          label="Status"
          value={
            <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2 py-0.5 rounded-full
              ${isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500' : 'bg-red-500'}`} />
              {isActive ? 'Active' : 'Inactive'}
            </span>
          }
        />
        {/* Leaving details only mean something once someone has left. */}
        {!isActive && (
          <>
            <FR label="Leaving Date"   value={fmtDate(leavingDate)} />
            <FR label="Leaving Reason" value={fmt(leavingReason)} />
          </>
        )}

        <SH label="Salary Range" icon={Award} />
        <FR label="Min Salary" value={e.HGD_MIN_SALARY ? `LKR ${Number(e.HGD_MIN_SALARY).toLocaleString()}` : '—'} />
        <FR label="Max Salary" value={e.HGD_MAX_SALARY ? `LKR ${Number(e.HGD_MAX_SALARY).toLocaleString()}` : '—'} />

        <SH label="Overtime" icon={Clock3Icon} />
        <FR label="Employee Type" value={({ Field: 'Field Staff', Staff: 'Office Staff' })[String(e.HED_EMPLOYEE_TYPE || '').trim()] || e.HED_EMPLOYEE_TYPE || '—'} />
        <FR label="OT Type" value={fmt(e.HED_OT_TYPE)} /> 
        <FR label="OT Rate"       value={e.HED_OT_RATE ? `LKR ${e.HED_OT_RATE}` : '—'} /> 

        {/* Shift moved over from the left column: Organisation alone is eight
            rows against three short blocks, which left this side half empty. */}
        <SH label="Shift" icon={Clock} />
        <FR label="Shift Name"   value={fmt(e.HSD_SHIFT_NAME)} />
        <FR label="Shift Code"   value={fmt(e.HSD_SHIFT_CODE)} />
        <FR label="Start Time"   value={fmt(e.HSD_START_TIME)} />
        <FR label="End Time"     value={fmt(e.HSD_END_TIME)} />
        {/* <FR label="Break"        value={d(e.HSD_BREAK_START_TIME) ? `${e.HSD_BREAK_START_TIME} – ${e.HSD_BREAK_END_TIME}` : '—'} /> */}
        <FR label="Full Day Hrs" value={e.HSD_FULL_DAY_HOURS ? `${e.HSD_FULL_DAY_HOURS} hrs` : '—'} />
        <FR label="Half Day Hrs" value={e.HSD_HALF_DAY_HOURS ? `${e.HSD_HALF_DAY_HOURS} hrs` : '—'} />

        {/* <SH label="Flags" icon={Shield} />
        <FR label="System User"    value={e.HED_IS_SYSTEM_USER ? 'Yes' : 'No'} />
        <FR label="Night Shift"    value={e.HSD_IS_NIGHT_SHIFT ? 'Yes' : 'No'} />
        <FR label="Flexible Shift" value={e.HSD_IS_FLEXIBLE_SHIFT ? 'Yes' : 'No'} />
        <FR label="Remarks"        value={fmt(e.HED_REMARKS || e.p_remarks)} /> */}
      </div>
    </div>
  );
}

// ─── Bank Panel ───────────────────────────────────────────────────────────────
function BankPanel({ e, bankTicks = {}, onToggleBank, canApproveBanks = false, busy = false }) {
  const banks = e.Banks || [];
  if (banks.length === 0) return (
    <EmptyState
      title="No bank accounts yet"
      message="Salary cannot be paid until an account is added. Use Edit to add one — it starts unapproved and needs approving before payroll picks it up."
    />
  );
  return (
    <div className="space-y-4">
      {banks.map((b, i) => {
        const approved = isBankApproved(b);
        const key      = bankKey(b) || String(i);
        const ticked   = !!bankTicks[key];
        return (
        <div key={b.HEB_EMPLOYEE_BANK_ID || i}
          className={`p-4 rounded-xl border transition-colors
            ${approved
              ? 'border-slate-100 bg-slate-50'
              : ticked
                ? 'border-primary-300 bg-primary-50/60'
                : 'border-amber-200 bg-amber-50/50'}`}>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <CreditCard size={14} className="text-blue-500 shrink-0" />
              <span className="text-[12px] font-bold text-slate-700 truncate">
                {b.HBD_BANK_NAME || 'Bank Account'} {isBankPrimary(b) ? '(Primary)' : ''}
              </span>
              {!isBankActive(b) && (
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-gray-200 text-gray-600 shrink-0">
                  Inactive
                </span>
              )}
            </div>

            {/* Approved accounts show a badge; pending ones show the tick box
                that arms them for the Approve button in the header bar. */}
            {approved ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full
                bg-primary-100 text-primary-700 shrink-0">
                <BadgeCheck size={11} />
                Approved
              </span>
            ) : canApproveBanks && onToggleBank ? (
              <label
                className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-lg cursor-pointer select-none shrink-0 border transition-colors
                  ${ticked
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-amber-700 border-amber-300 hover:border-amber-600'}
                  ${busy ? 'opacity-50 pointer-events-none' : ''}`}
                title="Tick to approve this bank account, then press Approve"
              >
                <input
                  type="checkbox"
                  className="w-3.5 h-3.5 accent-primary-600 cursor-pointer"
                  checked={ticked}
                  disabled={busy}
                  onChange={() => onToggleBank(key)}
                />
                {ticked ? 'Will be approved' : 'Approve this account'}
              </label>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-full
                bg-amber-100 text-amber-700 shrink-0">
                Pending Approval
              </span>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
            <div>
              <FR label="Bank"           value={fmt(b.HBD_BANK_NAME)} />
              <FR label="Bank Code"      value={fmt(b.HBD_BANK_CODE)} mono />
              {/* <FR label="SWIFT Code"     value={fmt(b.HBD_SWIFT_CODE)} mono /> */}
            </div>
            <div>
              <FR label="Branch"         value={fmt(b.HBB_BRANCH_NAME)} />
              <FR label="Branch Code"    value={fmt(b.HBB_BRANCH_CODE)} mono />
              <FR label="Account Name"   value={fmt(b.HEB_ACCOUNT_NAME)} />
              <FR label="Account Number" value={fmt(b.HEB_ACCOUNT_NUMBER)} mono />
              <FR label="Account Type"   value={fmt(b.HEB_ACCOUNT_TYPE)} />
            </div>
          </div>
        </div>
        );
      })}
    </div>
  );
}

// ─── Qualifications Panel ─────────────────────────────────────────────────────
function QualificationsPanel({ e, onPreviewDoc }) {
  const quals = (e.Qualifications || []).filter(q => d(q.HEQ_QUALIFICATION_NAME));
  if (quals.length === 0) return (
    <EmptyState
      title="No qualifications on record"
      message="Certificates and education history added through Edit will be listed here."
    />
  );
  return (
    <div className="space-y-3">
      {quals.map((q, i) => {
        const attUrl = q.HEQ_ATTACHMENT || q.heq_attachment || q.Attachment;
        return (
          <div key={q.HEQ_QUALIFICATION_ID || i} className="p-4 rounded-xl border border-slate-100 bg-slate-50">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[13px] font-bold text-slate-800">{q.HEQ_QUALIFICATION_NAME}</p>
              {attUrl && (
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => onPreviewDoc({ title: q.HEQ_QUALIFICATION_NAME, url: attUrl, fileName: `${q.HEQ_QUALIFICATION_NAME}.pdf` })}
                    className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold rounded-lg transition-colors"
                  >
                    <Eye size={12} /> View Certificate
                  </button>
                  <button
                    onClick={() => downloadFile(attUrl, `${q.HEQ_QUALIFICATION_NAME}.pdf`)}
                    className="flex items-center gap-1 px-2.5 py-1 text-[11px] bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-lg transition-colors"
                  >
                    <Download size={12} /> Download
                  </button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6">
              <FR label="Field of Study" value={fmt(q.HEQ_FIELD_OF_STUDY)} />
              <FR label="Institute"      value={fmt(q.HEQ_INSTITUTE)} />
              <FR label="Result"         value={fmt(q.HEQ_RESULT)} />
              <FR label="Grade"          value={fmt(q.HEQ_GRADE)} />
              {q.HEQ_GPA && <FR label="GPA" value={String(q.HEQ_GPA)} />}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main Modal ───────────────────────────────────────────────────────────────
// ─── Contract Details Panel ───────────────────────────────────────────────────
/* ── Contract dates ───────────────────────────────────────────────────────────────────
   The API sends .NET dates such as "12/31/2026 12:00:00 AM" (month first).
   They are read as local dates — never through toISOString(), which moves a
   Sri Lanka midnight back to the previous day. */
function ctParseDate(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  let m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM)?)?/i.exec(s);
  if (m) {
    const [, mo, dd, yy, hh = '0', mi = '0', ss = '0', ap = ''] = m;
    let h = Number(hh);
    if (/pm/i.test(ap) && h !== 12) h += 12;
    if (/am/i.test(ap) && h === 12) h = 0;
    return new Date(Number(yy), Number(mo) - 1, Number(dd), h, Number(mi), Number(ss));
  }
  m = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(s);
  if (m) {
    const [, yy, mo, dd, hh = '0', mi = '0', ss = '0'] = m;
    return new Date(Number(yy), Number(mo) - 1, Number(dd), Number(hh), Number(mi), Number(ss));
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

const ctDayOnly = (d) => (d ? new Date(d.getFullYear(), d.getMonth(), d.getDate()) : null);

const ctFmtDay = (d) =>
  d ? d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const ctFmtDayTime = (d) =>
  d
    ? `${ctFmtDay(d)}, ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
    : '—';

/** Whole months + days from a to b (b may be earlier → negative). */
function ctDiff(a, b) {
  if (!a || !b) return null;
  let from = ctDayOnly(a);
  let to = ctDayOnly(b);
  const sign = to < from ? -1 : 1;
  if (sign < 0) [from, to] = [to, from];
  // Adding months clamps to the month's last day, so 31 Dec + 6 months is
  // 30 Jun (not 1 Jul) and month-end contracts count in whole months.
  const addMonths = (d, n) => {
    const last = new Date(d.getFullYear(), d.getMonth() + n + 1, 0).getDate();
    return new Date(d.getFullYear(), d.getMonth() + n, Math.min(d.getDate(), last));
  };
  let months = (to.getFullYear() - from.getFullYear()) * 12 + (to.getMonth() - from.getMonth());
  let anchor = addMonths(from, months);
  if (anchor > to) {
    months -= 1;
    anchor = addMonths(from, months);
  }
  const days = Math.round((to - anchor) / 86400000);
  return { sign, months, days };
}

function ctSpan(diff, { signed = false } = {}) {
  if (!diff) return '—';
  const { sign, months, days } = diff;
  const y = Math.floor(months / 12);
  const mo = months % 12;
  const parts = [];
  if (y) parts.push(`${y} yr${y > 1 ? 's' : ''}`);
  if (mo) parts.push(`${mo} month${mo > 1 ? 's' : ''}`);
  if (days || !parts.length) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  const txt = parts.join(' ');
  if (!signed) return txt;
  return `${sign < 0 ? '−' : '+'}${txt}`;
}

const ctIsActiveRow = (r) =>
  !(r.HCTD_IS_ACTIVE === false || /^(false|0)$/i.test(String(r.HCTD_IS_ACTIVE ?? '').trim()));

/* ── Small pieces ─────────────────────────────────────────────────────────── */

function ContractSummaryCard({ icon: Icon, label, value, sub, tone = 'slate' }) {
  const tones = {
    slate: 'bg-slate-50 border-slate-200 text-slate-600',
    blue: 'bg-blue-50 border-blue-100 text-blue-600',
    green: 'bg-emerald-50 border-emerald-100 text-emerald-600',
    amber: 'bg-amber-50 border-amber-100 text-amber-600',
    red: 'bg-red-50 border-red-100 text-red-600',
  };
  return (
    <div className={`rounded-xl border p-3 ${tones[tone] || tones.slate}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
        <Icon size={12} /> {label}
      </div>
      <div className="mt-1 text-[13px] font-bold text-slate-900">{value}</div>
      {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function ContractTimelineItem({ dot, title, date, children, last = false }) {
  return (
    <li className="relative pl-8 pb-5">
      {!last && <span className="absolute left-[11px] top-6 bottom-0 w-px bg-slate-200" />}
      <span className={`absolute left-0 top-0.5 w-6 h-6 rounded-full flex items-center justify-center ${dot.cls}`}>
        <dot.icon size={12} />
      </span>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3">
        <span className="text-[12px] font-semibold text-slate-800">{title}</span>
        {date && <span className="text-[11px] text-slate-400">{date}</span>}
      </div>
      {children}
    </li>
  );
}

/* ── Panel ────────────────────────────────────────────────────────────────── */

/**
 * Contract Details tab — Contract employees only.
 * Start = date of joining; end-date changes come from
 * ContractorTermination/GetContractorTerminations, ordered by HCTD_CREATED_DATE.
 */
function ContractDetailsPanel({ e }) {
  const dispatch = useDispatch();
  const employeeId = e?.HED_EMPLOYEE_ID ?? e?.hedEmployeeId ?? e?.EmployeeId;

  // History — contractorTerminationSlice.
  const {
    employeeId: loadedFor,
    terminations,
    loading,
    error,
  } = useSelector((s) => s.contractorTermination);
  // Only use rows that belong to this employee.
  const rows = String(loadedFor) === String(employeeId) ? terminations : [];

  useEffect(() => {
    if (employeeId) dispatch(fetchContractorTerminations(employeeId));
  }, [dispatch, employeeId]);

  // Created-by names — employeeSlice (EMP/GetEmployees). HCTD_CREATED_BY is
  // an employee id, shown as that employee's HED_REPORT_NAME. The list is
  // usually already loaded by Employee Management; fetch it only if not.
  const allEmployees = useSelector((s) => s.employees?.employees || []);
  useEffect(() => {
    if (!allEmployees.length) dispatch(fetchAllEmployees());
  }, [dispatch, allEmployees.length]);

  const creatorNames = useMemo(() => {
    const ids = new Set(rows.map((r) => String(r.HCTD_CREATED_BY ?? '').trim()).filter(Boolean));
    const names = {};
    if (!ids.size) return names;
    allEmployees.forEach((emp) => {
      const id = String(emp.HED_EMPLOYEE_ID ?? '').trim();
      if (!ids.has(id)) return;
      names[id] =
        String(emp.HED_REPORT_NAME || '').trim() ||
        `${emp.HED_FIRST_NAME || ''} ${emp.HED_LAST_NAME || ''}`.trim();
    });
    return names;
  }, [rows, allEmployees]);

  // Oldest change first, by HCTD_CREATED_DATE (HCTD_ID breaks ties).
  const history = useMemo(
    () =>
      rows
        .filter(ctIsActiveRow)
        .map((r) => ({
          id: r.HCTD_ID,
          created: ctParseDate(r.HCTD_CREATED_DATE),
          oldEnd: ctParseDate(r.HCTD_OLD_TERMINATION_DATE),
          newEnd: ctParseDate(r.HCTD_NEW_TERMINATION_DATE),
          type: String(r.HCTD_CHANGE_TYPE || '').trim().toUpperCase(),
          reason: String(r.HCTD_REASON || '').trim(),
          remarks: String(r.HCTD_REMARKS || '').trim(),
          createdBy: String(r.HCTD_CREATED_BY || '').trim(),
        }))
        .sort((a, b) => (a.created?.getTime() ?? 0) - (b.created?.getTime() ?? 0) || (a.id ?? 0) - (b.id ?? 0)),
    [rows]
  );

  const start = ctParseDate(e?.HED_DATE_OF_JOINING || e?.p_recruitment_date);
  const latest = history[history.length - 1];
  const currentEnd = latest?.newEnd || ctParseDate(e?.HED_TERMINATION_DATE);
  // Original end = HCTD_OLD_TERMINATION_DATE of the oldest row. When that row
  // has no old date it is the row that first SET the end date, so its
  // HCTD_NEW_TERMINATION_DATE is the original end, not a shortening.
  const first = history[0];
  const originalEnd = first
    ? (first.oldEnd || first.newEnd)
    : ctParseDate(e?.HED_TERMINATION_DATE);
  // Real changes only: rows that have both an old and a new end date.
  const changes = history.filter((h) => h.oldEnd && h.newEnd);

  const isExtension = (h) =>
    h.oldEnd && h.newEnd ? h.newEnd > h.oldEnd : /EXTEN/.test(h.type);
  const extensions = changes.filter(isExtension).length;
  const shortenings = changes.length - extensions;

  const today = ctDayOnly(new Date());
  const ended = currentEnd && ctDayOnly(currentEnd) < today;
  const remaining = currentEnd && !ended ? ctDiff(today, currentEnd) : null;

  return (
    <div className="space-y-5">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ContractSummaryCard icon={CalendarCheck} label="Contract Start" value={ctFmtDay(start)} sub="Date of joining" tone="blue" />
        <ContractSummaryCard icon={Flag} label="Original End" value={ctFmtDay(originalEnd)}
          sub={start && originalEnd ? `Planned ${ctSpan(ctDiff(start, originalEnd))}` : null} />
        <ContractSummaryCard icon={CalendarX} label="Current End" value={ctFmtDay(currentEnd)}
          sub={ended ? 'Contract ended' : remaining ? `${ctSpan(remaining)} left` : null}
          tone={ended ? 'red' : 'green'} />
        <ContractSummaryCard icon={CalendarClock} label="Total Contract"
          value={start && currentEnd ? ctSpan(ctDiff(start, currentEnd)) : '—'}
          sub={changes.length
            ? `${extensions} extension${extensions !== 1 ? 's' : ''}, ${shortenings} shortening${shortenings !== 1 ? 's' : ''}`
            : 'No changes'}
          tone="amber" />
      </div>

      {/* Timeline */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <History size={12} className="text-blue-600" />
          <span className="text-[10px] font-bold text-blue-700 uppercase tracking-wider">Contract History</span>
          <div className="flex-1 h-px bg-blue-100" />
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[12px] text-slate-400 py-6 justify-center">
            <Loader2 size={14} className="animate-spin" /> Loading contract history…
          </div>
        ) : error ? (
          <div className="flex items-center gap-2 text-[12px] text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
            <AlertCircle size={14} /> {error}
          </div>
        ) : (
          <ol className="mt-1">
            <ContractTimelineItem
              dot={{ icon: CalendarCheck, cls: 'bg-blue-100 text-blue-600' }}
              title="Contract started"
              date={ctFmtDay(start)}
            />
            <ContractTimelineItem
              dot={{ icon: Flag, cls: 'bg-slate-100 text-slate-500' }}
              title="Original end date"
              date={ctFmtDay(originalEnd)}
            />

            {changes.map((h) => {
              const ext = isExtension(h);
              const change = ctDiff(h.oldEnd, h.newEnd);
              return (
                <ContractTimelineItem
                  key={h.id ?? `${h.created}`}
                  dot={ext
                    ? { icon: TrendingUp, cls: 'bg-emerald-100 text-emerald-600' }
                    : { icon: TrendingDown, cls: 'bg-amber-100 text-amber-600' }}
                  title={ext ? 'Contract extended' : 'Contract shortened'}
                  date={`Changed ${ctFmtDayTime(h.created)}`}
                >
                  <div className="mt-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <div className="flex flex-wrap items-center gap-2 text-[12px]">
                      <span className="text-slate-500 line-through">{ctFmtDay(h.oldEnd)}</span>
                      <span className="text-slate-400">→</span>
                      <span className="font-semibold text-slate-900">{ctFmtDay(h.newEnd)}</span>
                      <span className={`ml-auto text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                        ext ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                        {ctSpan(change, { signed: true })}
                      </span>
                    </div>
                    {(h.reason || h.remarks) && (
                      <div className="mt-1.5 space-y-0.5 text-[11px] text-slate-600">
                        {h.reason && <div><span className="text-slate-400">Reason:</span> {h.reason}</div>}
                        {h.remarks && <div><span className="text-slate-400">Remarks:</span> {h.remarks}</div>}
                      </div>
                    )}
                    {h.createdBy && (
                      <div className="mt-1 text-[10px] text-slate-400">
                        By {creatorNames[h.createdBy] || `user ${h.createdBy}`}
                      </div>
                    )}
                  </div>
                </ContractTimelineItem>
              );
            })}

            <ContractTimelineItem
              last
              dot={ended
                ? { icon: CalendarX, cls: 'bg-red-100 text-red-600' }
                : { icon: CalendarX, cls: 'bg-emerald-100 text-emerald-600' }}
              title={ended ? 'Contract ended' : 'Current end date'}
              date={ctFmtDay(currentEnd)}
            >
              {!changes.length && (
                <p className="mt-1 text-[11px] text-slate-400 italic">
                  The end date has not been extended or shortened.
                </p>
              )}
            </ContractTimelineItem>
          </ol>
        )}
      </div>
    </div>
  );
}

export default function EmployeeModal({
  employee,
  isOpen,
  onClose,
  onEdit,
  onApprove,
  onReject,
  approving = false,
  approvalError = null,
}) {
  const [activeTab, setActiveTab] = useState('overview');
  const [previewDoc, setPreviewDoc] = useState(null);

  const [bankTicks, setBankTicks] = useState({});
  // null until a decision button is pressed; then 'approve' or 'reject' while
  // the "are you sure" dialog is up.
  const [confirmKind, setConfirmKind] = useState(null);

  useEffect(() => {
    if (isOpen) { setActiveTab('overview'); setBankTicks({}); setConfirmKind(null); }
  }, [isOpen, employee]);

  const toggleBank = useCallback((key) => {
    setBankTicks((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleKey = useCallback((ev) => {
    if (ev.key !== 'Escape') return;

    setConfirmKind((k) => {
      if (k) return null;
      onClose();
      return null;
    });
  }, [onClose]);
  useEffect(() => {
    if (isOpen) { document.addEventListener('keydown', handleKey); document.body.style.overflow = 'hidden'; }
    return () => { document.removeEventListener('keydown', handleKey); document.body.style.overflow = ''; };
  }, [isOpen, handleKey]);

  if (!isOpen || !employee) return null;

  const e = employee;

  const rawSvc   = d(e.HED_SERVICE_NO) || d(e.p_ced_service_no);
  const firstName = d(e.HED_FIRST_NAME) || d(e.p_first_name) || '';
  const lastName  = d(e.HED_LAST_NAME)  || d(e.p_last_name)  || '';
  const title     = d(e.HED_TITLE)      || d(e.p_title)      || '';
  const name      = d(e.HED_REPORT_NAME) || d(e.p_report_name) ||
                    [title, firstName, lastName].filter(Boolean).join(' ') || 'Employee';
  const initials  = name.split(/[\s.]+/).filter(Boolean).slice(0, 2).map(p => p[0]).join('').toUpperCase() || '?';
  const isActive  = e.HED_IS_ACTIVE !== undefined
                    ? Boolean(e.HED_IS_ACTIVE)
                    : (!e.p_inactive_type || e.p_inactive_type === '' || e.p_inactive_type === '0');

  const designation = d(e.HED_DESIGNATION_NAME) || d(e.p_designation) || d(e.p_initial_category) || null;
  const dept        = d(e.HDD_DEPARTMENT_NAME) || d(e.p_department_code) || null;
  const location    = getLocationName(e) || null;
  const mobile      = d(e.HED_MOBILE_NO) || d(e.p_mobile) || null;
  const email       = d(e.HED_COMPANY_EMAIL) || d(e.HED_PERSONAL_EMAIL) || d(e.p_email) || null;
  const grade       = d(e.HGD_GRADE_NAME) || d(e.p_present_grade) || null;
  const directUrl   = getEmployeeProfilePicUrl(e);

  const joinDate    = d(e.HED_DATE_OF_JOINING) || d(e.p_recruitment_date);
  const yearsWorked = calcYears(joinDate);
  const awaitingApproval = isPendingApproval(e);
  // const gradient    = awaitingApproval ? PENDING_GRADIENT : HEADER_GRADIENT;

  const gradientClassName = awaitingApproval ? PENDING_GRADIENT : '';
  const gradientStyle = awaitingApproval ? undefined : {
    backgroundImage: `linear-gradient(to bottom right, ${tenant.theme.accent.solid}, ${tenant.theme.accent.dark})`
  }

  // Bank accounts carry their own approval flag, so a fully approved employee
  // can still have an account waiting. Both are decided from the same bar.
  const banksPending  = pendingBanksOf(e);
  const tickedBanks   = banksPending.filter((b) => bankTicks[bankKey(b)]);
  const showDecision  = (awaitingApproval || banksPending.length > 0) && (onApprove || onReject);
  // Nothing to send: employee already approved and no account ticked.
  const nothingToApprove = !awaitingApproval && tickedBanks.length === 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-slate-50 rounded-xl shadow-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">

        {/* ── Header ── */}
        <div className={`${gradientClassName} px-6 py-5 shrink-0`} style={gradientStyle}>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <ModalImage directUrl={directUrl} serviceNo={rawSvc} initials={initials} gradientClassName={gradientClassName} gradientStyle={gradientStyle} />
              <div>
                <h2 className="font-bold text-white text-lg leading-tight">{name}</h2>
                {designation && <p className="text-white/80 text-[13px] mt-0.5">{designation}</p>}
                <div className="flex items-center gap-2 mt-2 flex-wrap">
                  {rawSvc && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <Hash size={10} />#{rawSvc}
                    </span>
                  )}
                  {dept && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <Building2 size={10} />{dept}
                    </span>
                  )}
                  {location && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <MapPin size={10} />{location}
                    </span>
                  )}

                  {awaitingApproval ? (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm ring-1 bg-amber-400 text-amber-950 ring-amber-200/70">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-800 animate-pulse" />
                      Pending
                    </span>
                  ) : (
                    <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm ring-1
                      ${isActive
                        ? 'bg-white text-primary-700 ring-white/70'
                        : 'bg-red-500 text-white ring-red-300/70'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-white'}`} />
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                  {yearsWorked !== null && (
                    <span className="inline-flex items-center gap-1 bg-white/20 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                      <Clock size={10} />{yearsWorked} yr{yearsWorked !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-1.5 shrink-0">
              {onEdit && (
                <button onClick={onEdit}
                  className="flex items-center gap-1.5 text-white bg-white/15 hover:bg-white/25 transition-colors px-3 py-2 rounded-lg text-[12px] font-semibold"
                  aria-label="Edit employee">
                  <Pencil size={14} />
                  <span className="hidden sm:inline">Edit</span>
                </button>
              )}
              <button onClick={onClose}
                className="text-white/70 hover:text-white hover:bg-white/20 transition-colors p-2 rounded-lg"
                aria-label="Close">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Quick info row */}
          <div className="mt-3 pt-3 border-t border-white/20 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {mobile && (
              <div className="flex items-center gap-1.5">
                <Phone size={12} className="text-white/70" />
                <span className="text-white/90 text-[12px] font-medium">{mobile}</span>
              </div>
            )}
            {email && (
              <div className="flex items-center gap-1.5 col-span-2 sm:col-span-1">
                <Mail size={12} className="text-white/70" />
                <span className="text-white/90 text-[12px] font-medium truncate">{email}</span>
              </div>
            )}
            {grade && (
              <div className="flex items-center gap-1.5">
                <Award size={12} className="text-white/70" />
                <span className="text-white/90 text-[12px] font-medium">Grade: {grade}</span>
              </div>
            )}
            {d(e.HED_TOTAL_EXPERIANCE) && (
              <div className="flex items-center gap-1.5">
                <Clock size={12} className="text-white/70" />
                <span className="text-white/90 text-[12px] font-medium">Exp: {formatExperienceLong(e.HED_TOTAL_EXPERIANCE)}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Approval action bar ── */}
        {showDecision && (
          <div className="shrink-0 bg-amber-50 border-b border-amber-200 px-6 py-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-start gap-2.5">
                <span className="bg-amber-100 text-amber-700 p-1.5 rounded-lg shrink-0">
                  <ShieldAlert size={16} />
                </span>
                <div>
                  <p className="text-[13px] font-bold text-amber-900 leading-tight">
                    {awaitingApproval ? 'Awaiting your approval' : 'Bank details awaiting approval'}
                  </p>
                  <p className="text-[11px] text-amber-800/80 mt-0.5">
                    {awaitingApproval
                      ? 'Review the details below, then approve or reject this employee.'
                      : 'This employee is approved, but a bank account still needs a decision.'}
                  </p>

                  {/* Bank accounts are opt-in: only ticked ones are sent. */}
                  {banksPending.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setActiveTab('bank')}
                      className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-900"
                    >
                      <CreditCard size={11} />
                      {tickedBanks.length > 0
                        ? `${tickedBanks.length} of ${banksPending.length} bank account${banksPending.length !== 1 ? 's' : ''} ticked for approval`
                        : `${banksPending.length} bank account${banksPending.length !== 1 ? 's' : ''} pending — tick in the Bank tab`}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {/* {onReject && awaitingApproval && (
                  <button
                    type="button"
                    onClick={() => setConfirmKind('reject')}
                    disabled={approving}
                    title="Reject this employee"
                    className="flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg text-[12px] font-semibold
                      bg-white text-red-600 border border-red-200
                      hover:bg-red-600 hover:text-white hover:border-red-600 transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle size={14} />
                    Reject
                  </button>
                )} */}
                {onApprove && (
                  <button
                    type="button"
                    onClick={() => setConfirmKind('approve')}
                    disabled={approving || nothingToApprove}
                    title={nothingToApprove
                      ? 'Tick a bank account to approve it'
                      : 'Approve this employee'}
                    className="flex items-center justify-center gap-1.5 px-5 py-2 rounded-lg text-[12px] font-bold
                      bg-primary-600 text-white shadow-sm
                      hover:bg-primary-700 transition-colors
                      disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {approving
                      ? <Loader2 size={14} className="animate-spin" />
                      : <Check size={14} />}
                    {approving ? 'Saving…' : 'Approve'}
                  </button>
                )}
              </div>
            </div>

            {approvalError && (
              <p className="mt-2 text-[11px] font-medium text-red-600">{approvalError}</p>
            )}
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="flex border-b border-slate-200 bg-white shrink-0 overflow-x-auto">
          {TABS.filter((t) => !t.contractOnly || isContractCategory(e.HEC_CATEGORY_NAME || e.EmploymentCategoryName || e.employmentCategoryName)).map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-3 text-[12px] font-semibold whitespace-nowrap border-b-2 transition-colors
                ${activeTab === key ? 'border-blue-600 text-blue-700 bg-blue-50/60' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`}>
              <Icon size={13} className={activeTab === key ? 'text-blue-600' : ''} />
              {label}
              {/* Count badge so a pending account is visible from any tab. */}
              {key === 'bank' && banksPending.length > 0 && (
                <span className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-600 text-white text-[9px] font-bold">
                  {banksPending.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Body ──
            A fixed height, not flex-1: the tabs hold very different amounts of
            content (Employment is roughly three times Bank), and without it the
            whole dialog jumped size on every tab click. Short tabs now leave
            white space, long ones scroll inside. */}
        <div className="overflow-y-auto h-[clamp(360px,58vh,620px)] px-6 py-5 bg-white">
          {activeTab === 'overview'   && <OverviewPanel      e={e} onPreviewDoc={setPreviewDoc} />}
          {activeTab === 'documents'  && <DocumentsPanel     e={e} onPreviewDoc={setPreviewDoc} onEdit={onEdit} />}
          {activeTab === 'contact'    && <ContactPanel       e={e} />}
          {activeTab === 'employment' && <EmploymentPanel    e={e} />}
          {activeTab === 'bank'       && (
            <BankPanel
              e={e}
              bankTicks={bankTicks}
              onToggleBank={toggleBank}
              canApproveBanks={!!onApprove}
              busy={approving}
            />
          )}
          {activeTab === 'qualify'    && <QualificationsPanel e={e} onPreviewDoc={setPreviewDoc} />}
          {activeTab === 'contract'   && isContractCategory(e.HEC_CATEGORY_NAME || e.EmploymentCategoryName || e.employmentCategoryName) && <ContractDetailsPanel e={e} />}
        </div>

        {/* ── Footer ── */}
        <div className="shrink-0 bg-slate-50 border-t border-slate-200 px-6 py-3 flex items-center justify-between rounded-b-xl">
          <p className="text-[10px] text-slate-400">
            Press <kbd className="px-1 py-0.5 rounded border border-slate-300 font-mono text-[10px] bg-white">ESC</kbd> to close
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
              className="px-5 py-1.5 text-[12px] font-semibold rounded bg-slate-200 hover:bg-slate-300 text-slate-700 transition-colors">
              Close
            </button>
          </div>
        </div>
      </div>

      {/* ── "Are you sure?" step — nothing is sent until Yes is pressed ── */}
      <ConfirmDecision
        open={!!confirmKind}
        kind={confirmKind}
        name={name}
        includeEmployee={awaitingApproval}
        bankCount={tickedBanks.length}
        busy={approving}
        onCancel={() => setConfirmKind(null)}
        onConfirm={() => {
          const kind = confirmKind;
          setConfirmKind(null);
          if (kind === 'reject') onReject(e);
          else onApprove(e, tickedBanks);
        }}
      />

      {/* ── Document Lightbox Preview Modal ── */}
      {previewDoc && (
        <DocumentPreviewModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
        />
      )}
    </div>
  );
}